import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import { generateTags } from "@/lib/articleTags";

function scoreArticle(candidate, currentSlug, currentHub, currentTags, currentGuide, hubMap) {
  if (candidate.slug === currentSlug) return -1;
  let score = 0;

  // Same hub: +3
  const candidateHub = candidate.hub || hubMap[candidate.slug];
  if (candidateHub && candidateHub === currentHub) score += 3;

  // Shared tags: +2 per match
  const candidateTags = generateTags(candidate);
  const shared = currentTags.filter(t => candidateTags.includes(t));
  score += shared.length * 2;

  // Shared related guide: +1
  if (candidate.relatedGuide && candidate.relatedGuide === currentGuide) score += 1;

  return score;
}

const RelatedArticles = ({ currentSlug, count = 4 }) => {
  const { seoContent: SEO_CONTENT, articleHubs: ARTICLE_HUBS } = useContent();
  
  const articleHubMap = {};
  Object.entries(ARTICLE_HUBS).forEach(([hubId, hub]) => {
    (hub.articles || []).forEach(slug => { articleHubMap[slug] = hubId; });
  });

  const currentArticle = SEO_CONTENT[currentSlug];
  if (!currentArticle) return null;

  const currentHub = currentArticle.hub || articleHubMap[currentSlug];
  const currentTags = generateTags(currentArticle);
  const currentGuide = currentArticle.relatedGuide;

  const scored = Object.values(SEO_CONTENT)
    .map(article => ({
      article,
      score: scoreArticle(article, currentSlug, currentHub, currentTags, currentGuide, articleHubMap),
    }))
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, count);

  // Fallback: if not enough, grab any articles from the same hub or random
  if (scored.length < count) {
    const usedSlugs = new Set(scored.map(s => s.article.slug));
    usedSlugs.add(currentSlug);
    const fallback = Object.values(SEO_CONTENT)
      .filter(a => !usedSlugs.has(a.slug))
      .slice(0, count - scored.length)
      .map(article => ({ article, score: 0 }));
    scored.push(...fallback);
  }

  if (scored.length === 0) return null;

  return (
    <section className="section-padding bg-[#F2F0E9]" data-testid="related-articles-section">
      <div className="container-main">
        <h2 className="text-2xl font-semibold text-[#1A2018] mb-8">Keep Reading</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {scored.map(({ article }) => {
            const hubId = article.hub || articleHubMap[article.slug];
            const hubLabel = hubId ? ARTICLE_HUBS[hubId]?.shortTitle : null;
            const desc = article.content?.hook || article.content?.intro || article.metaDescription || "";

            return (
              <Link
                key={article.slug}
                to={`/articles/${article.slug}`}
                className="group bg-white rounded-2xl p-5 border border-gray-100 hover:border-[#2D6A4F] transition-all hover:shadow-md flex flex-col"
                data-testid={`related-article-${article.slug}`}
              >
                {hubLabel && (
                  <span className="inline-block self-start px-2.5 py-0.5 bg-[#E9EDC9] text-[#2D6A4F] rounded-full text-[10px] font-medium mb-3">
                    {hubLabel}
                  </span>
                )}
                <h3 className="font-semibold text-[#1A2018] leading-snug mb-2 line-clamp-2 group-hover:text-[#2D6A4F] transition-colors text-sm">
                  {article.title}
                </h3>
                <p className="text-xs text-[#4B5548] line-clamp-2 mb-3 flex-1">
                  {desc.slice(0, 130)}
                </p>
                <span className="text-xs text-[#2D6A4F] font-medium flex items-center gap-1 mt-auto">
                  Read more <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <Link to="/glp-guide" className="text-sm text-[#2D6A4F] font-medium hover:underline inline-flex items-center gap-1">
            View all articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RelatedArticles;
